import React, { useEffect, useState, useMemo, useCallback } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { api, fileUrl } from "../lib/api";
import SwissTopoMap from "../components/SwissTopoMap";
import SloveniaMap from "../components/SloveniaMap";
import ItalyMap from "../components/ItalyMap";
import { MapPin, ArrowLeft, Eye, EyeSlash, Stack, Plus, X, Pencil, Trash } from "@phosphor-icons/react";

// Per-country configuration. Adding another country = add an entry here.
// `MapComponent` is the Leaflet component used as the base view; everything
// else (artifact pins, personal pins, KML overlays) is country-agnostic.
const COUNTRIES = {
  it: {
    code: "it",
    label: "Italia",
    flag: "🇮🇹",
    MapComponent: ItalyMap,
    extraProps: {},
  },
  ch: {
    code: "ch",
    label: "Svizzera",
    flag: "🇨🇭",
    MapComponent: SwissTopoMap,
    extraProps: { initialOverlays: ["relief"] },
  },
  si: {
    code: "si",
    label: "Slovenia",
    flag: "🇸🇮",
    MapComponent: SloveniaMap,
    extraProps: {},
  },
};

export default function MapPage() {
  const params = useParams();
  // URL param `country` controls which country tab is active. Defaults to CH.
  const country = COUNTRIES[(params.country || "ch").toLowerCase()] || COUNTRIES.ch;
  const MapComponent = country.MapComponent;
  const [items, setItems] = useState([]);
  const [unlocated, setUnlocated] = useState([]);
  const [loading, setLoading] = useState(true);
  const [myLayers, setMyLayers] = useState([]);
  const [myPoints, setMyPoints] = useState([]);
  const [categories, setCategories] = useState([]); // [{id, label, color}]
  // Per-layer visibility (client-only, instant toggle without server round-trip)
  const [hiddenLayerIds, setHiddenLayerIds] = useState(new Set());
  // Per-category visibility for PERSONAL points only — useful to focus on
  // e.g. "just the priorità alta" while planning an outing.
  const [hiddenCategoryIds, setHiddenCategoryIds] = useState(new Set());
  // "Add point" mode: when active, the next map click opens the create modal
  const [addMode, setAddMode] = useState(false);
  // Modal state for both create and edit
  const [editing, setEditing] = useState(null); // { point_id?, lat, lng, name, description, category }
  const navigate = useNavigate();

  const refreshPoints = useCallback(async () => {
    const [layers, pts] = await Promise.all([
      api.get("/my-maps/layers"),
      api.get("/my-maps/points"),
    ]);
    setMyLayers(layers.data || []);
    setMyPoints(pts.data || []);
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const [g, all, layers, pts, cats] = await Promise.all([
          api.get("/artifacts/geo"),
          api.get("/artifacts"),
          api.get("/my-maps/layers"),
          api.get("/my-maps/points"),
          api.get("/my-maps/categories"),
        ]);
        setItems(g.data || []);
        const placed = new Set((g.data || []).map((i) => i.id));
        setUnlocated((all.data || []).filter((a) => !placed.has(a.id)));
        setMyLayers(layers.data || []);
        setMyPoints(pts.data || []);
        setCategories(cats.data || []);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const toggleLayer = (id) => {
    setHiddenLayerIds((s) => {
      const next = new Set(s);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const toggleCategory = (id) => {
    setHiddenCategoryIds((s) => {
      const next = new Set(s);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const visibleMyPoints = useMemo(
    () => myPoints.filter((p) => {
      if (hiddenLayerIds.has(p.layer_id)) return false;
      if (p.is_personal && hiddenCategoryIds.has(p.category)) return false;
      return true;
    }),
    [myPoints, hiddenLayerIds, hiddenCategoryIds],
  );

  // Map click handler — only meaningful when addMode is on
  const onMapClick = (latlng) => {
    if (!addMode) return;
    setEditing({
      point_id: null,
      lat: latlng.lat,
      lng: latlng.lng,
      name: "",
      description: "",
      category: "altro",
    });
    setAddMode(false);
  };

  // Personal point: enrich popup with edit/delete buttons
  const personalPointsForMap = useMemo(
    () => visibleMyPoints.map((p) => ({
      ...p,
      onEdit: p.is_personal ? () => setEditing({
        point_id: p.point_id,
        lat: p.lat,
        lng: p.lng,
        name: p.name,
        description: p.description,
        category: p.category || "altro",
      }) : undefined,
      onDelete: p.is_personal ? async () => {
        if (!window.confirm(`Eliminare il punto "${p.name}"?`)) return;
        await api.delete(`/my-maps/personal-points/${p.point_id}`);
        await refreshPoints();
      } : undefined,
    })),
    [visibleMyPoints, refreshPoints],
  );

  const saveEditing = async () => {
    if (!editing) return;
    const body = {
      name: editing.name.trim() || "Punto",
      description: editing.description || "",
      category: editing.category,
      lat: editing.lat,
      lng: editing.lng,
    };
    if (editing.point_id) {
      await api.patch(`/my-maps/personal-points/${editing.point_id}`, body);
    } else {
      await api.post(`/my-maps/personal-points`, body);
    }
    setEditing(null);
    await refreshPoints();
  };

  const markers = items
    .filter((it) => it.location?.lat && it.location?.lng)
    .map((it) => ({
      position: [it.location.lat, it.location.lng],
      code: it.inventory_code,
      onClick: () => navigate(`/artifact/${it.id}`),
      popup: (
        <div className="min-w-[160px]">
          <div className="font-mono text-xs bg-[#1E2520] text-white inline-block px-1.5 py-0.5 rounded-sm mb-1">
            {it.inventory_code}
          </div>
          <div className="font-serif text-base text-[#1E2520]">{it.identification?.nome}</div>
          <div className="text-xs text-[#5C635A] mt-0.5">{it.identification?.categoria}</div>
          {it.photos?.[0] && (
            <img src={fileUrl(it.photos[0])} alt="" className="w-full h-24 object-cover mt-2" />
          )}
          <Link to={`/artifact/${it.id}`} className="block text-xs text-[#9C5B42] mt-2 underline">
            Apri scheda →
          </Link>
        </div>
      ),
    }));

  return (
    <div className="max-w-7xl mx-auto px-5 md:px-8 py-5 md:py-10 fade-in">
      <Link to="/" className="inline-flex items-center gap-1.5 text-sm text-[#5C635A] hover:text-[#9C5B42] mb-3">
        <ArrowLeft size={16} /> Tutte le categorie
      </Link>
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3 mb-4 md:mb-6">
        <div>
          <div className="label-eyebrow mb-2 flex items-center gap-1.5">
            <MapPin weight="duotone" /> Mappa dei ritrovamenti
          </div>
          <h1 className="font-serif text-2xl md:text-5xl tracking-tight text-[#1E2520]">
            Dove hai trovato i tuoi tesori
          </h1>
          <p className="text-[#5C635A] mt-2 text-sm hidden md:block">
            Strati {country.label} + i tuoi segnalibri personali per pianificare le uscite.
          </p>

          {/* Country selector — switch between map providers without losing
              artifact pins / personal points which are country-agnostic.
              Uses theme-aware vars (--bg/--text) so the unselected tab
              stays readable in both light and dark modes. */}
          <div className="flex gap-1 mt-3" data-testid="country-tabs">
            {Object.values(COUNTRIES).map((c) => {
              const active = country.code === c.code;
              return (
                <Link
                  key={c.code}
                  to={c.code === "ch" ? "/map" : `/map/${c.code}`}
                  className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-sm border transition-colors ${
                    active
                      ? "bg-[#9C5B42] border-[#9C5B42] text-white"
                      : "country-tab-inactive"
                  }`}
                  data-testid={`country-tab-${c.code}`}
                >
                  <span aria-hidden>{c.flag}</span> {c.label}
                </Link>
              );
            })}
          </div>
        </div>
        <div className="flex md:flex-col md:items-end items-center justify-between gap-3">
          <div className="text-right text-sm text-[#5C635A]">
            <div><span className="font-mono text-[#1E2520]">{items.length}</span> reperti localizzati</div>
            {unlocated.length > 0 && (
              <div className="text-xs">
                <span className="font-mono text-[#9C5B42]">{unlocated.length}</span> da geolocalizzare
              </div>
            )}
          </div>
          <button
            onClick={() => setAddMode((v) => !v)}
            className={`inline-flex items-center gap-2 px-4 py-2 text-sm font-medium transition-colors ${
              addMode
                ? "bg-[#9C5B42] text-white"
                : "border border-[#9C5B42] text-[#9C5B42] hover:bg-[#FFF6EE]"
            }`}
            data-testid="add-point-btn"
          >
            {addMode ? <X size={16} /> : <Plus size={16} />}
            {addMode ? "Annulla" : "Aggiungi punto"}
          </button>
        </div>
      </div>

      {addMode && (
        <div
          className="mb-3 px-4 py-3 bg-[#FFF6EE] border border-[#9C5B42] text-[#7A4632] text-sm rounded-sm"
          data-testid="add-mode-banner"
        >
          <strong>Modalità inserimento attiva</strong>: clicca sulla mappa dove vuoi
          aggiungere un punto. Premi "Annulla" per uscire senza salvare.
        </div>
      )}

      <div className="card overflow-hidden mb-6">
        {loading ? (
          <div className="aspect-[16/9] scanning" />
        ) : (
          <MapComponent
            markers={markers}
            myMapsPoints={personalPointsForMap}
            height="min(calc(100dvh - 220px), 720px)"
            pickable={addMode}
            onPick={onMapClick}
            {...country.extraProps}
          />
        )}
      </div>

      {/* Category legend / toggles for personal points */}
      {categories.length > 0 && myPoints.some((p) => p.is_personal) && (
        <div className="card p-5 md:p-6 mb-6" data-testid="category-legend">
          <div className="label-eyebrow mb-3 flex items-center gap-1.5">
            <MapPin weight="duotone" size={14} /> Categorie segnalibri
          </div>
          <div className="flex flex-wrap gap-2">
            {categories.map((c) => {
              const hidden = hiddenCategoryIds.has(c.id);
              const count = myPoints.filter((p) => p.is_personal && p.category === c.id).length;
              return (
                <button
                  key={c.id}
                  onClick={() => toggleCategory(c.id)}
                  className={`inline-flex items-center gap-2 px-3 py-1.5 text-sm border transition-opacity ${
                    hidden ? "opacity-40" : ""
                  }`}
                  style={{ borderColor: c.color, color: hidden ? "#5C635A" : "#1E2520" }}
                  data-testid={`cat-toggle-${c.id}`}
                >
                  <span
                    className="w-3 h-3 rounded-full border border-white"
                    style={{ background: c.color, boxShadow: "0 0 0 1px rgba(0,0,0,0.35)" }}
                  />
                  {c.label}
                  <span className="text-xs text-[#5C635A] font-mono">({count})</span>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Imported layers toggle */}
      {myLayers.filter((l) => !l.is_personal).length > 0 && (
        <div className="card p-5 md:p-6 mb-8" data-testid="my-maps-panel">
          <div className="flex items-center gap-2 mb-3">
            <Stack weight="duotone" size={20} className="text-[#9C5B42]" />
            <div className="label-eyebrow">Le tue mappe (importate da Google)</div>
          </div>
          <p className="text-sm text-[#5C635A] mb-4">
            Clicca un layer per nasconderlo/mostrarlo. Per aggiungere o eliminare layer,
            vai in <Link to="/settings" className="text-[#9C5B42] underline">Impostazioni</Link>.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2">
            {myLayers.filter((l) => !l.is_personal).map((l) => {
              const hidden = hiddenLayerIds.has(l.id);
              return (
                <button
                  key={l.id}
                  onClick={() => toggleLayer(l.id)}
                  className={`flex items-center gap-3 px-3 py-2.5 border text-left transition-colors ${
                    hidden
                      ? "border-[#E5E0D8] bg-[#F9F8F6] text-[#9CA39A]"
                      : "border-[#9C5B42] bg-[#FFF6EE] text-[#1E2520]"
                  }`}
                  data-testid={`mymaps-toggle-${l.id}`}
                >
                  {hidden ? <EyeSlash size={18} /> : <Eye size={18} weight="duotone" className="text-[#9C5B42]" />}
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium truncate">{l.name}</div>
                    <div className="text-xs text-[#5C635A]">{l.point_count} punti</div>
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {unlocated.length > 0 && (
        <div className="card p-5 md:p-6">
          <div className="label-eyebrow mb-3">Reperti senza coordinate</div>
          <p className="text-sm text-[#5C635A] mb-4">
            Apri ciascuno e usa la sezione "Luogo del ritrovamento" per posizionarlo sulla mappa.
          </p>
          <ul className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {unlocated.map((it) => (
              <Link
                key={it.id}
                to={`/artifact/${it.id}`}
                className="card overflow-hidden hover:-translate-y-0.5 transition-transform"
              >
                {it.photos?.[0] && (
                  <img src={fileUrl(it.photos[0])} alt="" className="w-full aspect-square object-cover" />
                )}
                <div className="p-2 text-xs">
                  <span className="font-mono text-[#1E2520]">{it.inventory_code}</span>
                  <div className="truncate text-[#5C635A]">{it.identification?.nome}</div>
                </div>
              </Link>
            ))}
          </ul>
        </div>
      )}

      {/* Create/edit point modal */}
      {editing && (
        <PointModal
          editing={editing}
          setEditing={setEditing}
          onSave={saveEditing}
          categories={categories}
        />
      )}
    </div>
  );
}


function PointModal({ editing, setEditing, onSave, categories }) {
  const isNew = !editing.point_id;
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try { await onSave(); } finally { setSaving(false); }
  };

  return (
    <div
      className="fixed inset-0 z-[9999] bg-black/70 flex items-center justify-center px-4"
      onClick={() => setEditing(null)}
      data-testid="point-modal"
    >
      <form
        onSubmit={submit}
        className="card max-w-md w-full p-6 md:p-7 relative"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          onClick={() => setEditing(null)}
          className="absolute top-3 right-3 text-[#5C635A] hover:text-[#1E2520]"
          aria-label="Chiudi"
        >
          <X size={20} />
        </button>
        <h2 className="font-serif text-2xl text-[#1E2520] mb-1">
          {isNew ? "Nuovo punto" : "Modifica punto"}
        </h2>
        <div className="text-xs text-[#5C635A] font-mono mb-5">
          {editing.lat.toFixed(5)}, {editing.lng.toFixed(5)}
        </div>

        <label className="label-eyebrow block mb-2">Nome</label>
        <input
          type="text"
          className="input-base mb-4"
          value={editing.name}
          onChange={(e) => setEditing({ ...editing, name: e.target.value })}
          placeholder="Es: Campo vicino al castello"
          maxLength={120}
          autoFocus
          required
          data-testid="point-name"
        />

        <label className="label-eyebrow block mb-2">Categoria</label>
        <div className="grid grid-cols-1 gap-1.5 mb-4">
          {categories.map((c) => {
            const selected = editing.category === c.id;
            return (
              <label
                key={c.id}
                className={`flex items-center gap-3 px-3 py-2 border cursor-pointer transition-colors ${
                  selected ? "bg-[#FFF6EE]" : "bg-white hover:bg-[#F9F8F6]"
                }`}
                style={{ borderColor: selected ? c.color : "#E5E0D8" }}
              >
                <input
                  type="radio"
                  name="category"
                  value={c.id}
                  checked={selected}
                  onChange={() => setEditing({ ...editing, category: c.id })}
                  className="sr-only"
                />
                <span
                  className="w-4 h-4 rounded-full border-2 border-white shrink-0"
                  style={{ background: c.color, boxShadow: "0 0 0 1px rgba(0,0,0,0.35)" }}
                />
                <span
                  className="text-sm font-medium"
                  style={{ color: selected ? c.color : "#1E2520" }}
                >
                  {c.label}
                </span>
              </label>
            );
          })}
        </div>

        <label className="label-eyebrow block mb-2">Note (opzionali)</label>
        <textarea
          className="input-base min-h-[80px] mb-5"
          value={editing.description}
          onChange={(e) => setEditing({ ...editing, description: e.target.value })}
          placeholder="Es: parlare prima con il proprietario"
          maxLength={500}
          data-testid="point-description"
        />

        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => setEditing(null)}
            className="btn-ghost flex-1"
          >
            Annulla
          </button>
          <button
            type="submit"
            disabled={saving}
            className="btn-primary flex-1"
            data-testid="point-save"
          >
            {saving ? "Salvataggio..." : isNew ? "Salva punto" : "Aggiorna"}
          </button>
        </div>
      </form>
    </div>
  );
}
